import { createCachePromise } from '../../../utils/function';
import { EMP_CODE_KEY, getAvatarByAccount, getEmpCode } from '../../../utils/auth';
import { removeCookie } from '../../../utils/cookie';
import { getUserInfo } from '../../../apis/methods/user';
import type { RoleItem, UserInfo } from './types';
import { getRolePermissionData, ROLE_MULTI_CODE } from './adapter';
import { useSettingsStore } from '../..';
import { defineStore } from 'pinia';
import { ref, toRefs, reactive, computed } from 'vue';
import { bus } from '@sfvine/main-qiankun';
import { pluginsConfig as plugins } from '../../../plugins';

const useUserStore = defineStore('user', () => {
  const originUserInfo = ref<Record<string, any>>({}) // 接口返回的原始数据
  const userInfo = reactive<UserInfo>({
    empCode: getEmpCode() || '', // 用户id，工号
    empName: '', // 用户姓名
    avatar: '', // 用户头像
    transitDepotNo: '', // 用户网点
    position: '', // 岗位信息
  });

  const DEFAULT_ROLE_ITEM = { roleId: '', roleCode: '', roleName: '无权限', rolePosition: '', permissions: [] as string[] }
  // 角色权限数据
  const roleData = ref<RoleItem[]>([]);
  const currentRole = ref({ ...DEFAULT_ROLE_ITEM })
  const roleId = computed(() => currentRole.value.roleId)
  const roleCode = computed(() => currentRole.value.roleCode)
  const roleName = computed(() => currentRole.value.roleName)
  const rolePosition = computed(() => currentRole.value.rolePosition)
  const permissions = computed(() => currentRole.value.permissions)
  // 当前角色code集合，多角色时有多个code
  const roles = computed(() => {
    return roleData.value
      .filter((item) => {
        if (currentRole.value.roleCode === ROLE_MULTI_CODE) {
          return item.roleCode !== ROLE_MULTI_CODE;
        }
        return currentRole.value.roleCode === item.roleCode;
      })
      .map((item) => item.roleCode)
  });

  const settingsStore = useSettingsStore();

  /**
   * 切换角色
   */
  const switchRole = (newRoleCode: string) => {
    const target = roleData.value.find((item) => item.roleCode === newRoleCode);   
    currentRole.value = !target ? { ...DEFAULT_ROLE_ITEM } : {
      roleId: target.roleId,
      roleCode: target.roleCode,
      roleName: target.roleName,
      rolePosition: target.position,
      permissions: target.permissions || []
    }

    if (settingsStore.settings.role.shouldCacheCurrentRole) {
      // 记住本次选择的角色
      sessionStorage.setItem('lastRoleCode', currentRole.value.roleCode);
    }
  };

  /**
   * !用户侧 api，请谨慎修改入参及返回
   * 更新角色 list
   * @param data 角色 list
   * @param defaultRoleCode 默认角色编码
   */
  const updateRoleDataForUser = (data: RoleItem[], defaultRoleCode?: string) => {
    roleData.value = data;

    // 只排除 undefined 和 null
    if (defaultRoleCode != null) {
      switchRole(defaultRoleCode)
    }
  };

  /**
   * 初始化，多次调用只会执行一次，永远会resolve，不会reject
   * @param refresh 强制刷新
   * @return {Promise}
   */
  const initUser = createCachePromise(async () => {
    const initBaseInfo = async () => {
      // 有登录态时获取权限
      const res = await getUserInfo();
      if (!res?.obj) {
        return
      }

      const { empCode, empName, transitDepotNo, position } = res.obj

      // 处理用户头像
      const avatar = res.obj.avatar ?? await getAvatarByAccount(empCode)

      originUserInfo.value = res.obj;
      userInfo.empCode = empCode;
      userInfo.empName = empName;
      userInfo.avatar = avatar;
      userInfo.transitDepotNo = transitDepotNo;
      userInfo.position = position;

      await plugins.onUserDataUpdate({ operatorNo: empCode, operatorName: empName, transitDepotNo, dutyName: position })
    }
    const initRole = async () => {
      const { settings } = settingsStore;
      const baseOn = settings.menu?.baseOn || 'routes';
      const subSystemCode = settings.basp.baspSubSystemCode;
      roleData.value = await getRolePermissionData(baseOn, subSystemCode || '');
      if (!roleData.value.length) {
        return
      }

      // 设置默认角色
      // 优先级：缓存中的角色 > isDefaultRole > 第一个角色
      let defaultRole: string | null = null
      if (settings.role.shouldCacheCurrentRole) {
        defaultRole = sessionStorage.getItem('lastRoleCode')
        const isValid = roleData.value.find(item => item.roleCode === defaultRole)
        if (!isValid) {
          defaultRole = null
        }
      }
      if (!defaultRole) {
        defaultRole = roleData.value.find(item => item.isDefaultRole === '1')?.roleCode || roleData.value[0]?.roleCode;
      }
      switchRole(defaultRole);
    }

    try {
      await initBaseInfo()
      await initRole()
    } catch (e) {
      console.error(e);
    }
  });

  /**
   * todo 处理退出登录逻辑
   */
  const logout = async () => {
    const {
      settings: { casConfig, login },
    } = settingsStore;
    const loginTypeRes = typeof login.type === 'function' ? await login.type() : login.type
    // const cas = new CAS(casConfig as any);
    await login?.onBeforeLogout?.(loginTypeRes);
    try {
      await login?.onLogout?.(loginTypeRes, casConfig);
      login?.onAfterLogout?.(loginTypeRes);
      bus.$emit('logoutSuccess');

      const sensors = (window as any).sensorsDataAnalytic201505;
      sensors?.logout()
    } catch (e) {
      bus.$emit('logoutFailed', e);
    }

    // 清除缓存
    [EMP_CODE_KEY, 'lastRoleCode', 'moduleId'].forEach((item) => {
      window.sessionStorage.removeItem(item);
    });
    [`CAS_LOGIN_RES__${casConfig.appKey}`].forEach((item) => {
      window.localStorage.removeItem(item);
    });
    ['userId'].forEach((item) => {
      removeCookie(item);
    });
  };

  return {
    ...toRefs(userInfo),
    originUserInfo,
    roleId,
    roleCode,
    roleName,
    rolePosition,
    permissions,
    roles,
    roleData,
    switchRole,
    updateRoleDataForUser,
    initUser,
    logout,
  };
});

export default useUserStore;
